import React, { useLayoutEffect, useRef, useState } from "react";
import "./CoachBubble.css";

// A one-time hint's bubble (founder, build 44 — lib/welcomeHints), today under
// the Request Price box on Requests. Calm on purpose ("chill, it's a welcome
// tour"): it fades in once, and the thing it points at gets the .coach-glow
// breath, never a pulse. It sits in the page's flow, so it never covers
// anything, and its arrow is measured onto the anchor wherever the layout
// puts it. A tap does what the anchor does.

type Props = {
  /** What the arrow points at. */
  anchor: HTMLElement | null;
  onTap: () => void;
  children: React.ReactNode;
};

const EDGE = 16; // the page's side gutter
const ARROW_INSET = 18; // keeps the arrow clear of the rounded corners

export default function CoachBubble({ anchor, onTap, children }: Props) {
  const rowRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ left: number; arrow: number } | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const row = rowRef.current;
      const bubble = bubbleRef.current;
      if (!row || !bubble || !anchor) return;
      const r = row.getBoundingClientRect();
      const a = anchor.getBoundingClientRect();
      const w = bubble.offsetWidth;
      const center = a.left + a.width / 2 - r.left;
      const left = Math.max(EDGE, Math.min(r.width - EDGE - w, center - w / 2));
      const arrow = Math.max(ARROW_INSET, Math.min(w - ARROW_INSET, center - left));
      setPos((p) => (p && p.left === left && p.arrow === arrow ? p : { left, arrow }));
    };
    place();
    window.addEventListener("resize", place);
    // The anchor changes width as it's edited ("$5" → "$150").
    const ro = typeof ResizeObserver !== "undefined" && anchor ? new ResizeObserver(place) : null;
    if (ro && anchor) ro.observe(anchor);
    return () => {
      window.removeEventListener("resize", place);
      ro?.disconnect();
    };
  }, [anchor, children]);

  return (
    <div className="coach" ref={rowRef}>
      <button
        type="button"
        ref={bubbleRef}
        className="coach-bubble"
        style={
          pos
            ? ({ marginLeft: pos.left, "--coach-arrow": `${pos.arrow}px` } as React.CSSProperties)
            : { visibility: "hidden" }
        }
        onClick={onTap}
      >
        {children}
      </button>
    </div>
  );
}
